<template>
  <section class="fly-schedule-list">
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <b-autocomplete
              ref="airlineSelect"
              v-model="selectedAirline"
              name="airline"
              field="display_name"
              :data="airlines"
              :placeholder="
                $t('message.please_select') + $t('attributes.airline')
              "
              :open-on-focus="true"
              clearable
              @typing="onSelectingAirline"
              @select="onSelectedAirline"
            >
              <template #default="props">
                <div class="media">
                  <div class="media-left" style="width: 20px">
                    {{ props.option.code }}
                  </div>
                  <div class="media-content">
                    {{ props.option.name }}({{ props.option.english_name }})
                  </div>
                </div>
              </template>
              <template #empty>
                No results found
              </template>
            </b-autocomplete>
          </b-field>

          <b-field>
            <b-autocomplete
              ref="departureCitySelect"
              v-model="selectedDepartureCity"
              name="departure_city"
              field="display_name"
              :data="departureCities"
              :placeholder="
                $t('message.please_select') + $t('attributes.departure_city')
              "
              :open-on-focus="true"
              clearable
              @typing="onSelectingDepartureCity"
              @select="onSelectedDepartureCity"
            >
              <template #default="props">
                <div class="media">
                  <div class="media-left" style="width: 20px">
                    {{ props.option.code }}
                  </div>
                  <div class="media-content">
                    {{ props.option.name }}({{ props.option.english_name }})
                  </div>
                </div>
              </template>
              <template #empty>
                No results found
              </template>
            </b-autocomplete>
          </b-field>

          <b-field>
            <b-autocomplete
              ref="arriveCitySelect"
              v-model="selectedArriveCity"
              name="arrive_city"
              field="display_name"
              :data="arriveCities"
              :placeholder="
                $t('message.please_select') + $t('attributes.arrive_city')
              "
              :open-on-focus="true"
              clearable
              @typing="onSelectingArriveCity"
              @select="onSelectedArriveCity"
            >
              <template #default="props">
                <div class="media">
                  <div class="media-left" style="width: 20px">
                    {{ props.option.code }}
                  </div>
                  <div class="media-content">
                    {{ props.option.name }}({{ props.option.english_name }})
                  </div>
                </div>
              </template>
              <template #empty>
                No results found
              </template>
            </b-autocomplete>
          </b-field>

          <b-field>
            <b-input
              v-model="search"
              :placeholder="$t('attributes.search') + $t('attributes.fly_no')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') + $t('attributes.fly_schedule') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      class="fly-schedule-table"
      :data="items"
      :loading="isLoading"
      :mobile-cards="false"
      scrollable
      hoverable
      narrowed
      bordered
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="id"
        :label="$t('attributes.id')"
        width="150"
        sortable
        centered
        v-slot="props"
      >
        <p class="has-text-weight-medium">{{ props.row.id }}</p>
      </b-table-column>

      <b-table-column
        field="effect_date"
        :label="$t('attributes.effect_date')"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.effect_date }}
      </b-table-column>

      <b-table-column
        field="fly_no"
        :label="$t('attributes.fly_no')"
        width="120"
        sortable
        v-slot="props"
      >
        {{ props.row.fly_no }}
      </b-table-column>

      <b-table-column
        field="airline"
        :label="$t('attributes.airline')"
        width="120"
        sortable
        v-slot="props"
      >
        <p class="nowrap">
          {{ props.row.airline }}({{ props.row.airline_code }})
        </p>
      </b-table-column>

      <b-table-column
        field="departure_city"
        :label="$t('attributes.departure_city')"
        width="120"
        sortable
        v-slot="props"
      >
        <p class="nowrap">
          {{ props.row.departure_city }}({{ props.row.dep_city }})
        </p>
      </b-table-column>

      <b-table-column
        field="arrive_city"
        :label="$t('attributes.arrive_city')"
        width="120"
        sortable
        v-slot="props"
      >
        <p class="nowrap">
          {{ props.row.arrive_city }}({{ props.row.arr_city }})
        </p>
      </b-table-column>

      <b-table-column
        field="departure_time"
        :label="$t('attributes.departure_time')"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.departure_time }}
      </b-table-column>

      <b-table-column
        field="arrive_time"
        :label="$t('attributes.arrive_time')"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.arrive_time }}
      </b-table-column>

      <b-table-column
        field="estimated_days"
        :label="$t('attributes.estimated_days')"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.estimated_days }}
      </b-table-column>

      <b-table-column
        field="mon"
        :label="$t('attributes.mon')"
        sortable
        centered
        v-slot="props"
      >
        <b-icon
          :icon="flyStatusIcon(props.row.mon)"
          :type="flyStatusColor(props.row.mon)"
          size="is-small"
        ></b-icon>
      </b-table-column>

      <b-table-column
        field="tue"
        :label="$t('attributes.tue')"
        sortable
        centered
        v-slot="props"
      >
        <b-icon
          :icon="flyStatusIcon(props.row.tue)"
          :type="flyStatusColor(props.row.tue)"
          size="is-small"
        ></b-icon>
      </b-table-column>

      <b-table-column
        field="wen"
        :label="$t('attributes.wen')"
        sortable
        centered
        v-slot="props"
      >
        <b-icon
          :icon="flyStatusIcon(props.row.wen)"
          :type="flyStatusColor(props.row.wen)"
          size="is-small"
        ></b-icon>
      </b-table-column>

      <b-table-column
        field="thr"
        :label="$t('attributes.thr')"
        sortable
        centered
        v-slot="props"
      >
        <b-icon
          :icon="flyStatusIcon(props.row.thr)"
          :type="flyStatusColor(props.row.thr)"
          size="is-small"
        ></b-icon>
      </b-table-column>

      <b-table-column
        field="fri"
        :label="$t('attributes.fri')"
        sortable
        centered
        v-slot="props"
      >
        <b-icon
          :icon="flyStatusIcon(props.row.fri)"
          :type="flyStatusColor(props.row.fri)"
          size="is-small"
        ></b-icon>
      </b-table-column>

      <b-table-column
        field="sat"
        :label="$t('attributes.sat')"
        sortable
        centered
        v-slot="props"
      >
        <b-icon
          :icon="flyStatusIcon(props.row.sat)"
          :type="flyStatusColor(props.row.sat)"
          size="is-small"
        ></b-icon>
      </b-table-column>

      <b-table-column
        field="sun"
        :label="$t('attributes.sun')"
        sortable
        centered
        v-slot="props"
      >
        <b-icon
          :icon="flyStatusIcon(props.row.sun)"
          :type="flyStatusColor(props.row.sun)"
          size="is-small"
        ></b-icon>
      </b-table-column>

      <b-table-column :width="80" label="功能" centered v-slot="props">
        <div class="buttons">
          <b-button type="is-danger" @click.prevent="onDelete(props.row)">
            {{ $t('attributes.delete') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      :width="1200"
      has-modal-card
    >
      <fly-schedule-form :fly-schedule="selectedItem" @send="onSend" />
    </b-modal>

    <b-modal
      :active.sync="isDeleteModalActive"
      :can-cancel="['escape', 'x']"
      :width="960"
      has-modal-card
    >
      <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
          <p class="modal-card-title has-text-danger-dark">此航班不可刪除！</p>
          <button
            type="button"
            class="delete"
            @click="isDeleteModalActive = false"
          />
        </header>
        <section class="modal-card-body">
          <div v-if="products.length > 0" class="is-size-6">
            <p class="is-size-5 has-text-danger has-text-weight-bold">
              已被下列「團別」引用
            </p>
            <div class="table-container">
              <table class="table is-fullwidth">
                <thead>
                  <tr>
                    <th>團號</th>
                    <th>團名</th>
                    <th>狀態</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="product in products" :key="product.code">
                    <td class="nowrap">
                      <a
                        :href="`/products?status=all&code=${product.code}`"
                        target="_blank"
                      >
                        {{ product.code }}
                      </a>
                    </td>
                    <td class="nowrap" style="width: 400px;">
                      <p v-html="product.name"></p>
                    </td>
                    <td
                      class="nowrap"
                      :class="{
                        'has-text-danger': !product.shelve_on
                      }"
                    >
                      {{ product.shelve_on ? '上架' : '下架' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div
            v-if="travel_groups.length > 0"
            style="margin-top: 12px; padding-top: 12px; border-top: 2px solid #e4e4e4;"
          >
            <p class="is-size-5 has-text-danger has-text-weight-bold">
              已被下列「旅行團」引用
            </p>
            <div class="table-container">
              <table class="table is-fullwidth">
                <thead>
                  <tr>
                    <th>團號</th>
                    <th>團名</th>
                    <th>狀態</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="group in travel_groups" :key="group.code">
                    <td class="nowrap">
                      <a
                        :href="`/travel-groups?exp=true&code=${group.code}`"
                        target="_blank"
                      >
                        {{ group.code }}
                      </a>
                    </td>
                    <td class="nowrap" style="width: 400px;">
                      <p v-html="group.name"></p>
                    </td>
                    <td
                      class="nowrap"
                      :class="{
                        'has-text-danger': !group.shelve_on
                      }"
                    >
                      {{ group.shelve_on ? '上架' : '下架' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <div class="buttons has-addons is-right" style="width: 100%;">
            <b-button
              label="Ok"
              type="is-danger"
              style="width: 120px;"
              @click="isDeleteModalActive = false"
            />
          </div>
        </footer>
      </div>
    </b-modal>

    <b-loading :is-full-page="true" :active.sync="removing"></b-loading>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { AIRLINE_API, CITY_TEMPLATES_API, FLY_SCHEDULE_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import FlyScheduleForm from './FlyScheduleForm'

export default {
  name: 'FlyScheduleList',
  components: { FlyScheduleForm },

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,

      search: this.query.search || '',
      sortField: this.query.sort || 'effect_date',
      sortOrder: this.query.order || 'desc',
      defaultSortOrder: 'asc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      loadingAirlines: false,
      airlines: [],
      selectedAirline: null,
      selectedAirlineCode: this.query.ac || null,

      loadingCities: false,
      departureCities: [],
      selectedDepartureCity: null,
      selectedDepartureCode: this.query.dc || null,
      arriveCities: [],
      selectedArriveCity: null,
      selectedArriveCode: this.query.rc || null,

      isFormModalActive: false,
      sending: false,

      isDeleteModalActive: false,
      products: [],
      travel_groups: [],
      removing: false
    }
  },
  computed: {
    isLoading() {
      return this.loadingCities || this.loadingAirlines || this.loadingItems
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      await this.getAirlines(this.selectedAirlineCode)
      await this.getDepartureCities(this.selectedDepartureCode)
      await this.getArriveCities(this.selectedArriveCode)

      if (this.selectedAirlineCode) {
        const x = this.airlines.find(a => a.code === this.selectedAirlineCode)
        this.$refs.airlineSelect.setSelected(x)
        this.selectedAirline = x && x.display_name
        this.selectedAirlineCode = x && x.code
      }

      if (this.selectedDepartureCode) {
        const x = this.departureCities.find(
          a => a.code === this.selectedDepartureCode
        )
        this.$refs.departureCitySelect.setSelected(x)
        this.selectedDepartureCity = x && x.display_name
        this.selectedDepartureCode = x && x.code
      }

      if (this.selectedArriveCode) {
        const x = this.arriveCities.find(
          a => a.code === this.selectedArriveCode
        )
        this.$refs.arriveCitySelect.setSelected(x)
        this.selectedArriveCity = x && x.display_name
        this.selectedArriveCode = x && x.code
      }

      await this.loadItems()
    },
    async getAirlines(search) {
      const api = `${AIRLINE_API}/list`
      const params = {
        search: search
      }

      try {
        const res = await this.axios.get(api, { params })
        this.airlines = res.data.data.map(air => ({
          ...air,
          code: air.code.trim(),
          name: air.name.trim(),
          english_name: air.english_name ? air.english_name.trim() : '',
          display_name: this.formatAirlineName(air)
        }))
      } catch (e) {
        this.airlines = []
        this.selectedAirline = null
        this.selectedAirlineCode = null

        notifyService.fetch_error('airline', e)
      }
    },
    onSelectingAirline: debounce(function(search) {
      this.selectedAirlineCode = null
      this.getAirlines(search)
    }, 500),
    onSelectedAirline(option) {
      if (!option && this.selectedAirline && this.selectedAirline.length > 0) {
        return
      }

      this.selectedAirlineCode = option && option.code
      this.updateQuery()
      this.loadItems()
    },

    formatCityName(city) {
      if (!city) {
        return ''
      }

      return `${city.code.trim()} - ${city.name.trim()}(${city.english_name})`
    },
    async getDepartureCities(search) {
      const api = `${CITY_TEMPLATES_API}/list`
      const params = {
        search: search
      }

      try {
        const res = await this.axios.get(api, { params })
        this.departureCities = res.data.data.map(city => ({
          ...city,
          code: city.code.trim(),
          name: city.name.trim(),
          display_name: this.formatCityName(city)
        }))
      } catch (e) {
        this.departureCities = []
        this.selectedDepartureCity = null
        this.selectedDepartureCode = null

        notifyService.fetch_error('city', e)
      }
    },
    onSelectingDepartureCity: debounce(function(search) {
      this.selectedDepartureCode = null
      this.getDepartureCities(search)
    }, 500),
    onSelectedDepartureCity(option) {
      if (!option && this.selectedDepartureCity) {
        return
      }

      this.selectedDepartureCode = option && option.code
      this.updateQuery()
      this.loadItems()
    },

    async getArriveCities(search) {
      const api = `${CITY_TEMPLATES_API}/list`
      const params = {
        search: search
      }

      try {
        const res = await this.axios.get(api, { params })
        this.arriveCities = res.data.data.map(city => ({
          ...city,
          code: city.code.trim(),
          name: city.name.trim(),
          display_name: this.formatCityName(city)
        }))
      } catch (e) {
        this.arriveCities = []
        this.selectedArriveCity = null
        this.selectedArriveCode = null

        notifyService.fetch_error('city', e)
      }
    },
    onSelectingArriveCity: debounce(function(search) {
      this.selectedArriveCode = null
      this.getArriveCities(search)
    }, 500),
    onSelectedArriveCity(option) {
      if (!option && this.selectedArriveCity) {
        return
      }

      this.selectedArriveCode = option && option.code
      this.updateQuery()
      this.loadItems()
    },

    async loadItems() {
      try {
        const api = `${FLY_SCHEDULE_API}`
        const params = {
          search: this.search,
          airline_code: this.selectedAirlineCode,
          dep_city: this.selectedDepartureCode,
          arr_city: this.selectedArriveCode,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('fly_schedule', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.search = query.search || ''
      this.selectedAirlineCode = query.ac || ''
      this.selectedDepartureCode = query.dc || ''
      this.selectedArriveCode = query.rc || ''
      this.sortField = query.sort || 'effect_date'
      this.sortOrder = query.order || 'desc'
      this.page = query.page || 1
      this.perPage = query.page_size || 10
    },
    updateQuery() {
      const query = {
        search: this.search,
        ac: this.selectedAirlineCode,
        dc: this.selectedDepartureCode,
        rc: this.selectedArriveCode,
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilter: debounce(function() {
      this.updateQuery()
      this.loadItems()
    }, 500),
    onPageChange(page) {
      this.page = page

      this.updateQuery()
      this.loadItems()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order

      this.updateQuery()
      this.loadItems()
    },

    onAdd() {
      this.selectedItem = {
        id: null,
        fly_no: null,
        effect_date: null,
        airline_code: null,
        dep_city: null,
        arr_city: null,
        departure_hour: null,
        departure_min: null,
        arrive_hour: null,
        arrive_min: null,
        mon: 'true',
        tue: 'true',
        wen: 'true',
        thr: 'true',
        fri: 'true',
        sat: 'true',
        sun: 'true',
        estimated_days: 0
      }
      this.isFormModalActive = true
    },
    onModify(item) {
      this.selectedItem = item
      this.isFormModalActive = true
    },
    async onDelete(item) {
      const api =
        `${FLY_SCHEDULE_API}/${item.id}` +
        `?fly_no=${item.fly_no.trim()}` +
        `&effect_date=${item.effect_date}`

      try {
        this.removing = true
        const res = await this.axios.get(api)
        const result = res.data

        if (!result.success) {
          notifyService.error('無法連上後台伺服器！')
        } else {
          const schedule = result.data
          if (
            schedule.products.length > 0 ||
            schedule.travel_groups.length > 0
          ) {
            this.products = schedule.products
            this.travel_groups = schedule.travel_groups
            this.isDeleteModalActive = true
          } else {
            this.$buefy.dialog.confirm({
              message: this.$t('attributes.confirm_delete'),
              type: 'is-danger',
              confirmText: '刪除航班',
              onConfirm: () => {
                this.deleteItem(item)
              }
            })
          }
        }
      } catch (e) {
        notifyService.error(e)
      } finally {
        this.removing = false
      }
    },
    onSend(item) {
      if (item.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        this.sending = true
        await this.axios.post(FLY_SCHEDULE_API, item)
        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${FLY_SCHEDULE_API}/${item.id}`

        this.sending = true
        const res = await this.axios.put(api, item)
        const _item = res.data.data

        const idx = this.items.findIndex(s => s.id === _item.id)
        const items = [...this.items]
        items[idx] = _item
        this.items = items

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async deleteItem(item) {
      try {
        const api =
          `${FLY_SCHEDULE_API}/${item.id}` +
          `?fly_no=${item.fly_no.trim()}` +
          `&effect_date=${item.effect_date}`

        this.sending = true
        await this.axios.delete(api)
        notifyService.success(this.$t('attributes.delete_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.delete_error(e)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style lang="scss">
.fly-schedule-list {
  .fly-schedule-table {
    margin-top: 0.75rem;

    td {
      vertical-align: middle;
    }
  }
}
</style>
